import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import FileUploader from "../../components/documents/FileUploader"
import { ProgressionBar } from "../../components/state/ProgressionBar"
import Survey from "../../components/surveys/Survey"
import SurveyListCard from "../../components/surveys/SurveyListCard"
import SurveyModal from "../../components/surveys/SurveyModal"
import { useAuth } from "../../hooks/useAuth"
import { useDocumentsQuery } from "../../queries/useDocuments"
import {
  useActiveAndNotCompletedPatientsSurveys,
  useCompletedPatientsSurveys,
  useSurveys
} from "../../queries/useSurveys"
import { IPatientSurvey } from "../../types/types"
import { useAccountStore } from "../../utils/store"
import { usePatient } from "../../queries/usePatients"
import { SurgeryCategory } from "../SurgeryCategory"
import { Loader } from "../../components/state/Loader"

enum FlowPhase {
  INTRO = "INTRO",
  SURVEYS = "SURVEYS",
  DOCUMENTS = "DOCUMENTS",
  OUTRO = "OUTRO",
  ANSWERS = "ANSWERS",
  STOP = "STOP"
}

const determinePhase = (
  patientSurveysLength: number | undefined,
  unAnsweredPatientSurveysLength: number | undefined,
  medicalDocumentsLength: number | undefined
) => {
  if (
    (unAnsweredPatientSurveysLength && !medicalDocumentsLength) ||
    !patientSurveysLength
  )
    return FlowPhase.INTRO
  if (!unAnsweredPatientSurveysLength && !medicalDocumentsLength)
    return FlowPhase.DOCUMENTS
  if (!unAnsweredPatientSurveysLength && medicalDocumentsLength)
    return FlowPhase.OUTRO
  else return FlowPhase.ANSWERS
}
const Home = () => {
  const queryClient = useQueryClient()
  const scrollRef = useRef<null | HTMLDivElement>(null)
  const { authenticate } = useAuth()
  const { account } = useAccountStore()
  const { data: patient, isLoading: isPatientLoading } = usePatient(
    account?.patient_id
  )
  const { data: patientsSurveys, isLoading: isPatientSurveysLoading } =
    useCompletedPatientsSurveys(
      account?.patient_id?.toString() || "",
      account?.funnel,
      patient?.surgery_category,
      patient?.recommended
    )

  const {
    data: unAnsweredPatientSurveys,
    isLoading: isUnAnsweredPatientSurveysLoading
  } = useActiveAndNotCompletedPatientsSurveys(
    account?.patient_id?.toString() || "",
    account?.funnel,
    patient?.surgery_category,
    patient?.recommended
  )
  const { data: surveysDefinition, isLoading: isSurveysLoading } = useSurveys()
  const { data: medicalDocuments, isLoading: isMedicalDocumentsLoading } =
    useDocumentsQuery(account?.patient_id?.toString() || "")

  const [showSurveyModal, setShowSurveyModal] = useState(false)
  const [patientSurvey, setPatientSurvey] = useState<IPatientSurvey>()
  const [flowPhase, setFlowPhase] = useState<FlowPhase>()

  const [patientSurveyVisible, setPatientSurveyVisible] = useState<number>(
    unAnsweredPatientSurveys?.[0]?.id || 0
  )
  const [completion, setCompletion] = useState<number>(0)

  useEffect(() => {
    if (
      !(
        isPatientSurveysLoading ||
        isSurveysLoading ||
        isMedicalDocumentsLoading ||
        isUnAnsweredPatientSurveysLoading
      )
    )
      setFlowPhase(
        determinePhase(
          patientsSurveys?.length,
          unAnsweredPatientSurveys?.length,
          medicalDocuments?.length
        )
      )
  }, [
    patientsSurveys,
    unAnsweredPatientSurveys,
    medicalDocuments,
    isPatientSurveysLoading,
    isSurveysLoading,
    isMedicalDocumentsLoading,
    isUnAnsweredPatientSurveysLoading
  ])

  useEffect(() => {
    authenticate()
  }, [authenticate])

  useEffect(() => {
    setPatientSurveyVisible(unAnsweredPatientSurveys?.[0]?.id || 0)
    setCompletion(
      patientsSurveys && unAnsweredPatientSurveys
        ? Math.round(
            (1 -
              unAnsweredPatientSurveys?.length /
                (unAnsweredPatientSurveys.length + patientsSurveys?.length)) *
              100
          )
        : 0
    )
  }, [unAnsweredPatientSurveys, patientsSurveys])

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth"
    })
  }, [patientSurveyVisible])

  const pickSurvey = (surveyReference: string) => {
    if (surveysDefinition) {
      const _survey = surveysDefinition?.find(
        (_surv) => _surv.survey_reference === surveyReference
      )
      return _survey
    }
  }

  const getBody = () => {
    switch (flowPhase) {
      case FlowPhase.INTRO:
        return renderIntro()
      case FlowPhase.SURVEYS:
        return renderSurveys()
      case FlowPhase.DOCUMENTS:
        return renderDocuments()
      case FlowPhase.OUTRO:
        return renderOutro()
      case FlowPhase.ANSWERS:
        return renderAnswers()
      case FlowPhase.STOP:
      default:
        return null
    }
  }

  const renderIntro = () => (
    <div className="flex flex-col items-center justify-center h-full mx-4 text-center md:mx-0 gap-2">
      <h1 className="text-xl font-semibold text-cortexx-900">
        Prenez le temps de bien lire et de répondre à toutes les questions
        posées.
      </h1>
      <h2 className="font-extralight text-normal text-cortexx-900 text-center">
        Ces questions vont permettre à votre chirurgien de mieux préparer votre
        rendez-vous, et vous prendront environ 5 minutes au total.
      </h2>
      <h2 className="font-extralight text-normal text-cortexx-900 text-center mt-4">
        A la fin du questionnaire il vous sera demandé de charger un
        compte-rendu d'imagerie (scanner ou IRM), ce document est{" "}
        <span className="font-bold">obligatoire</span>.
      </h2>
      <h2 className="font-extralight text-normal text-cortexx-900 text-center">
        Munissez-vous d'une photo ou d'une copie du document, ou encore du pdf
        avant de commencer.
      </h2>
      <h2 className="font-extralight text-normal text-cortexx-900 text-center mt-4">
        Si vous ne chargez pas ce document, vous ne pourrez pas obtenir de
        rendez-vous anticipé via cette plateforme.
      </h2>
      <h2 className="font-extralight text-normal text-cortexx-900 text-center">
        Si vous n'avez pas encore fait d'IRM/Scanner, vous pouvez demander à
        votre médecin traitant de vous en prescire un.
      </h2>
      <button
        className="mt-4 bg-cortexx-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-cortexx-550"
        onClick={() => {
          if (unAnsweredPatientSurveys?.length) setFlowPhase(FlowPhase.SURVEYS)
          else if (!medicalDocuments?.length) setFlowPhase(FlowPhase.DOCUMENTS)
          else setFlowPhase(FlowPhase.OUTRO)
          setPatientSurveyVisible(unAnsweredPatientSurveys?.[0]?.id || 0)
        }}
        type="button"
      >
        Aller aux questions
      </button>
    </div>
  )

  const renderSurveys = () => {
    if (!!unAnsweredPatientSurveys?.length) {
      return (
        <div className="flex flex-col w-screen h-full justify-center items-center">
          <ProgressionBar completion={completion} />
          <div className="flex snap-x snap-mandatory overflow-x-hidden w-screen md:w-[750px] h-full justify-start">
            <div className="flex flex-row">
              {unAnsweredPatientSurveys.map((_patientSurvey, index) => (
                <div
                  className="flex items-center md:mx-4"
                  ref={
                    _patientSurvey.id === patientSurveyVisible
                      ? scrollRef
                      : null
                  }
                >
                  <Survey
                    key={_patientSurvey.id}
                    patientSurvey={_patientSurvey}
                    patientId={account?.patient_id || 0}
                    changeSurvey={() => {
                      if (index === unAnsweredPatientSurveys.length - 1) {
                        queryClient.invalidateQueries({
                          queryKey: ["patient_surveys"]
                        })
                      } else {
                        setCompletion(
                          patientsSurveys
                            ? Math.round(
                                ((patientsSurveys?.length + index + 1) /
                                  (unAnsweredPatientSurveys.length +
                                    patientsSurveys?.length)) *
                                  100
                              )
                            : 0
                        )
                        setPatientSurveyVisible(
                          unAnsweredPatientSurveys[index + 1]?.id
                        )
                      }
                    }}
                    isUp={_patientSurvey.id === patientSurveyVisible}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    setFlowPhase(FlowPhase.DOCUMENTS)
    return null
  }

  const renderOutro = () => (
    <div className="flex flex-col w-screen h-full justify-center items-center overflow-auto">
      <div className="flex flex-col w-11/12 md:w-1/2 rounded-xl p-8 bg-cortexx-50 border-slate-300 border justify-center mx-4 shadow-md">
        <h1 className="font-bold text-cortexx-900 mb-2">
          Merci pour vos informations, elles ont été envoyées à votre chirurgien
          pour préparer votre rendez-vous.
        </h1>
        <h2 className="font-normal text-cortexx-900">
          Lors de votre consultation, pensez à amener vos examens d'imagerie.
        </h2>
        {account?.funnel === "filter_phase_2" ? (
          <>
            <h1 className="font-bold text-cortexx-900 mt-6 mb-2">
              Un email qui confirme votre demande vous a été envoyé avec
              l'adresse info@cortexxmi.com.
            </h1>
            <h2 className="font-normal text-cortexx-900">
              Pensez à vérifier vos spams et à autoriser l'adresse
              info@cortexxmi.com si vous ne voyez pas cet email.
            </h2>
            <h1 className="font-bold text-cortexx-900 mt-6 mb-2">
              Un email vous sera envoyé avec la date de votre rendez-vous
              définitif.
            </h1>
            <h2 className="font-normal text-cortexx-900">
              Une fois que votre chirurgien aura pris connaissance de vos
              données et analysé votre dossier, vous recevrez un autre email
              vous indiquant la date de votre rendez-vous définitif.
            </h2>
          </>
        ) : null}
        <div className="flex flex-col w-full items-center justify-center">
          <button
            className="mt-8 bg-cortexx-500 text-white font-semibold py-4 px-6 rounded-lg w-full md:w-1/4"
            onClick={() => setFlowPhase(FlowPhase.ANSWERS)}
            type="button"
          >
            Aller au résumé
          </button>
        </div>
      </div>
    </div>
  )

  const renderAnswers = () =>
    patientsSurveys?.length ? (
      <div className="flex flex-col w-full mx-4 items-center text-start h-full">
        <h1 className="text-l font-semibold text-cortexx-900 mb-4 md:w-96">
          Résumé de vos questionnaires
        </h1>
        <div className="overflow-y-auto no-scrollbar pt-4">
          {patientsSurveys.map((_patientSurvey) => (
            <div className="mb-4">
              <SurveyListCard
                patientSurvey={_patientSurvey}
                openSurvey={() => {
                  setShowSurveyModal(true)
                  setPatientSurvey(_patientSurvey)
                }}
              />
            </div>
          ))}
        </div>
      </div>
    ) : null

  const renderDocuments = () => (
    <FileUploader
      patientId={account?.patient_id?.toString() || ""}
      goNext={() => setFlowPhase(FlowPhase.OUTRO)}
    />
  )

  return (
    <div className="flex w-full bg-cortexx-50 flex-1 flex-wrap justify-center items-center h-1/2 pt-2">
      {!isPatientLoading &&
      !isUnAnsweredPatientSurveysLoading &&
      !isPatientSurveysLoading &&
      !isMedicalDocumentsLoading &&
      !isSurveysLoading ? (
        patient?.surgery_category === "no_category" ? (
          <SurgeryCategory />
        ) : (
          getBody()
        )
      ) : (
        <Loader />
      )}
      {showSurveyModal && patientSurvey ? (
        <SurveyModal
          closeModal={() => setShowSurveyModal(false)}
          survey={pickSurvey(patientSurvey.survey_reference)}
          patientSurvey={patientSurvey}
        />
      ) : null}
    </div>
  )
}

export default Home
